import { useState, useCallback, useRef } from 'react';

/**
 * Comprehensive loading manager that tracks ALL async operations
 * including room models, artwork models, textures, etc.
 */
export const useLoadingManager = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState(new Set());
  const operationIdCounter = useRef(0);

  // Start a loading operation and return an ID to track it
  const startLoading = useCallback((description = 'Loading...') => {
    const operationId = `operation_${++operationIdCounter.current}`;
    // Starting loading operation silently
    
    setLoadingOperations(prev => {
      const newSet = new Set(prev);
      newSet.add({ id: operationId, description });
      return newSet;
    });
    
    setIsLoading(true);
    return operationId;
  }, []);

  // Finish a loading operation
  const finishLoading = useCallback((operationId) => {
    // Finishing loading operation silently
    
    setLoadingOperations(prev => {
      const newSet = new Set();
      for (const op of prev) {
        if (op.id !== operationId) {
          newSet.add(op);
        }
      }
      return newSet;
    });

    // Check if this was the last operation
    setLoadingOperations(current => {
      if (current.size === 0) {
        // All loading operations complete silently
        setIsLoading(false);
      } else {
        // Loading operations remaining
      }
      return current;
    });
  }, []);

  // Force finish all operations (for error recovery)
  const finishAllLoading = useCallback(() => {
    console.log('🛑 Force finishing all loading operations');
    setLoadingOperations(new Set());
    setIsLoading(false);
  }, []);

  // Get current loading status
  const getLoadingStatus = useCallback(() => {
    return {
      isLoading,
      operationCount: loadingOperations.size,
      operations: Array.from(loadingOperations)
    };
  }, [isLoading, loadingOperations]);

  return {
    isLoading,
    startLoading,
    finishLoading,
    finishAllLoading,
    getLoadingStatus
  };
};